import BookIndex from "components/shared/book/list";
import { Transition } from "components/shared/column";
import SharedReactTable from "components/shared/reactTable";
import { dateToView } from "helpers/date";
import { paths } from "helpers/paths";
import { DateRangeColumnFilter, type FilterProps, rowIndexColumn, SelectColumnFilter } from "helpers/reactTable";
import { tableContainer } from "helpers/redux";
import { getWaybillCarLogistName } from "helpers/waybills";
import React, { FC, MouseEvent } from "react";
import { Link } from "react-router-dom";
import { list as employeesList } from "sources/books/employees";
import type { WaybillListModel } from "sources/waybills";
import { getWaybillRegistryStatusLabel, WaybillStatusFilter } from "./status";

type WaybillParticipantKey = "cargoOwner" | "consignee" | "carrier";

export { getWaybillCarLogistName };

const participantColumn = (Header: string, participantKey: WaybillParticipantKey) => ({
    Header,
    id: `${participantKey}/fullName`,
    accessor: (item: WaybillListModel) => item[participantKey]?.fullName || "",
    filterable: true,
    sortable: true,
});

class WaybillsList extends BookIndex {
    state: any;
    props: any;

    get tableColumns() {
        return [
            rowIndexColumn,
            {
                Header: "Номер ЭТрН",
                accessor: "billNumber",
                filterable: true,
                sortable: true,
                Cell: ({ value, row: { original } }: { value: string; row: { original: WaybillListModel } }) => (
                    <Link
                        to={paths.waybillsShow(original.id)}
                        onClick={(event: MouseEvent<HTMLAnchorElement>) => event.stopPropagation()}
                    >
                        {value}
                    </Link>
                ),
            },
            {
                Header: "Дата",
                accessor: "billDate",
                filterable: true,
                filterType: "dateRange",
                sortable: true,
                Filter: DateRangeColumnFilter,
                Cell: ({ value }: { value: string }) => dateToView(value),
                CSVExport: ({ value }: { value: string }) => dateToView(value),
            },
            {
                Header: "Статус",
                id: "waybillStatus",
                accessor: getWaybillRegistryStatusLabel,
                filterable: true,
                filterType: "prepared",
                sortable: true,
                sortKey: "lastTitleType",
                Filter: WaybillStatusFilter,
            },
            participantColumn("Отправитель", "cargoOwner"),
            participantColumn("Получатель", "consignee"),
            participantColumn("Перевозчик", "carrier"),
            {
                Header: "Гос. номер ТС",
                accessor: "carNumber",
                filterable: true,
                sortable: true,
            },
            {
                Header: "Логист",
                id: "carLogist/id",
                accessor: getWaybillCarLogistName,
                filterable: true,
                filterType: "select",
                sortable: false,
                Filter: (props: FilterProps) => (
                    <SelectColumnFilter
                        {...props}
                        selectProps={{
                            url: employeesList,
                            inputProps: { name: "carLogist/id" },
                            propName: "fullNameOrEmail",
                        }}
                    />
                ),
            },
            {
                Header: "Водитель",
                accessor: "driverName",
                filterable: true,
                sortable: true,
            },
            {
                Header: "Связанная заявка",
                accessor: "bidId",
                filterable: true,
                filterType: "int",
                sortable: true,
                Cell: ({ value }: { value?: number | null }) =>
                    value ? (
                        <Link
                            to={paths.bidShow("bid", value)}
                            onClick={(event: MouseEvent<HTMLAnchorElement>) => event.stopPropagation()}
                        >
                            Заявка №{value}
                        </Link>
                    ) : (
                        "Не связана"
                    ),
            },
        ];
    }

    renderHead = () => (
        <div className="book-list__head">
            <button
                type="button"
                className="book-list__head-close close close--sm border-0 bg-transparent"
                onClick={this.close}
                aria-label="Закрыть реестр ЭТрН"
            />
            <div className="book-list__head-title">Накладные (ЭТрН){this.renderCounter()}</div>
        </div>
    );

    renderListComponent = () => {
        const hasFilters = Object.values(this.props.params.customFilters || {}).some(
            (filter: any) =>
                filter?.value &&
                (filter.filterType !== "prepared" ||
                    filter.value.status !== "all" ||
                    filter.value.signatureRequired !== "all"),
        );
        return (
            <SharedReactTable
                {...this.props}
                columns={this.state.tableColumns}
                tableProps={{
                    fitTableToContainer: true,
                    noDataText: hasFilters ? "По заданным фильтрам ЭТрН не найдены" : "Реестр ЭТрН пока пуст",
                }}
            />
        );
    };

    render() {
        return (
            <div className="page-content">
                <Transition dir="full">
                    <div className="page-book__list book-list list">
                        {this.renderHead()}
                        <div className="container-fluid my-2">{this.renderActions()}</div>
                        {this.renderListComponent()}
                    </div>
                </Transition>
            </div>
        );
    }
}

export default tableContainer<WaybillListModel>(WaybillsList as unknown as FC, "waybills");
